/**
 * Ecranele de configurare din consola de platformă:
 *  • Planuri & abonamente — prețuri, perioade de probă, limite, funcționalități (nimic hardcodat);
 *  • Feature flags — module active implicit și pe planuri;
 *  • Audit — jurnalul acțiunilor sensibile;
 *  • Sistem — evenimente interne (erori 5xx, joburi eșuate).
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  cx,
  useToast,
} from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { AUDIT_LABELS, formatCurrency, formatDateTime, formatNumber, relativeTime, useResource } from '../../lib/hooks.js';
import { errorMessage } from '../../lib/session.js';
import { AdminCard, AdminHeader, AdminRow, AdminTable, AdminTableSkeleton, Td } from './AdminKit.js';

/* ─────────────────────────────── planuri ─────────────────────────────── */

interface AdminPlan {
  id: string;
  code: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
  trial_days: number;
  is_public: boolean;
  is_active: boolean;
  features: string[];
  limits: Record<string, number>;
  sort_order: number;
  subscribers: number;
}

interface FeatureFlagRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  module: string;
  default_enabled: boolean;
  plan_availability: string[];
  is_active: boolean;
  overrides: number;
}

const LIMIT_HINTS: Record<string, string> = {
  restaurants: 'restaurante',
  locations: 'locații',
  menus: 'meniuri',
  products: 'produse',
  qrcodes: 'coduri QR',
  displays: 'ecrane',
  users: 'utilizatori',
  storage_mb: 'stocare (MB)',
  languages: 'limbi',
};

export function AdminPlans() {
  const toast = useToast();
  const plans = useResource<AdminPlan[]>(() => api.get<AdminPlan[]>('/admin/plans'));
  const flags = useResource<FeatureFlagRow[]>(() => api.get<FeatureFlagRow[]>('/admin/feature-flags'));
  const [editing, setEditing] = useState<AdminPlan | null>(null);
  const [busy, setBusy] = useState(false);

  // Funcționalitățile disponibile vin din feature flags — nu există listă hardcodată în UI.
  const knownFeatures = useMemo(
    () => (flags.data ?? []).map((flag) => ({ code: flag.code, name: flag.name })),
    [flags.data],
  );

  return (
    <div className="page-enter">
      <AdminHeader
        title="Planuri & abonamente"
        subtitle="Prețurile, perioadele de probă și limitele se administrează aici — nu sunt scrise în cod."
      />

      {plans.error ? (
        <InlineAlert tone="danger" title="Planurile nu au putut fi încărcate">
          {plans.error}
        </InlineAlert>
      ) : null}

      {plans.loading ? (
        <AdminCard>
          <AdminTableSkeleton rows={4} />
        </AdminCard>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {(plans.data ?? []).map((plan) => (
            <AdminCard key={plan.id} className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-[20px] text-white">{plan.name}</h2>
                    <span className="font-mono text-caption text-ink-400">{plan.code}</span>
                  </div>
                  <span className="text-body-s text-ink-200 nums">
                    {plan.price_cents === 0 ? 'Gratuit' : `${formatCurrency(plan.price_cents, plan.currency)} / ${plan.billing_interval}`}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {plan.is_public ? <Badge tone="brand">public</Badge> : <Badge tone="neutral">ascuns</Badge>}
                  {!plan.is_active ? <Badge tone="warning">inactiv</Badge> : null}
                  <span className="text-caption text-ink-400 nums">{plan.subscribers} abonați</span>
                </div>
              </div>

              <p className="text-body-s text-ink-400">{plan.description}</p>

              <div className="flex flex-wrap gap-1.5">
                {plan.features.length ? (
                  plan.features.map((feature) => (
                    <span key={feature} className="rounded-full border border-white/10 px-2.5 py-1 text-caption text-ink-200">
                      {knownFeatures.find((item) => item.code === feature)?.name ?? feature}
                    </span>
                  ))
                ) : (
                  <span className="text-caption text-ink-400">fără funcționalități incluse</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-white/5 pt-3 sm:grid-cols-3">
                {Object.entries(plan.limits).map(([key, value]) => (
                  <div key={key} className="flex flex-col">
                    <span className="text-caption text-ink-400">{LIMIT_HINTS[key] ?? key}</span>
                    <span className="text-body-s text-white nums">{value === -1 ? 'nelimitat' : formatNumber(value)}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between border-t border-white/5 pt-3">
                <span className="text-caption text-ink-400">
                  {plan.trial_days > 0 ? `${plan.trial_days} zile de probă` : 'fără perioadă de probă'}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="border-white/15 bg-white/5 text-white"
                  onClick={() => setEditing(plan)}
                >
                  Editează planul
                </Button>
              </div>
            </AdminCard>
          ))}
        </div>
      )}

      {editing ? (
        <PlanEditor
          plan={editing}
          features={knownFeatures}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            setBusy(true);
            try {
              await api.patch(`/admin/plans/${editing.id}`, patch);
              toast.success(`Planul „${editing.name}” a fost actualizat`, 'Modificările se aplică imediat, inclusiv limitelor deja afișate în dashboard-uri.');
              setEditing(null);
              await plans.reload();
            } catch (error) {
              toast.error('Planul nu a putut fi salvat', errorMessage(error));
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function PlanEditor({
  plan,
  features,
  busy,
  onClose,
  onSave,
}: {
  plan: AdminPlan;
  features: Array<{ code: string; name: string }>;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description);
  const [price, setPrice] = useState((plan.price_cents / 100).toFixed(2));
  const [trialDays, setTrialDays] = useState(String(plan.trial_days));
  const [isPublic, setIsPublic] = useState(plan.is_public);
  const [isActive, setIsActive] = useState(plan.is_active);
  const [overageMode, setOverageMode] = useState<'block' | 'soft' | 'notify'>('block');
  const [limits, setLimits] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(plan.limits).map(([key, value]) => [key, String(value)])),
  );
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(plan.features);

  const priceCents = Math.round(Number.parseFloat(price.replace(',', '.')) * 100);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Editează planul ${plan.name}`}
      description="Modificările se aplică imediat: limitele sunt citite din baza de date la fiecare cerere."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Anulează
          </Button>
          <Button
            loading={busy}
            onClick={() =>
              void onSave({
                name: name.trim(),
                description: description.trim(),
                priceCents: Number.isFinite(priceCents) ? priceCents : plan.price_cents,
                trialDays: Number.parseInt(trialDays, 10) || 0,
                isPublic,
                isActive,
                overageMode,
                limits: Object.fromEntries(
                  Object.entries(limits).map(([key, value]) => [key, Number.parseInt(value, 10) || 0]),
                ),
                features: selectedFeatures,
              })
            }
          >
            Salvează
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nume plan">
            {(props) => <Input {...props} value={name} onChange={(event) => setName(event.target.value)} maxLength={60} />}
          </Field>
          <Field label="Preț" hint={`${plan.currency} / ${plan.billing_interval}`}>
            {(props) => (
              <Input
                {...props}
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                inputMode="decimal"
                className="nums"
              />
            )}
          </Field>
        </div>

        <Field label="Descriere" hint="Apare pe pagina publică de prezentare.">
          {(props) => (
            <Textarea {...props} value={description} onChange={(event) => setDescription(event.target.value)} rows={3} maxLength={600} />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Zile de probă">
            {(props) => (
              <Input {...props} value={trialDays} onChange={(event) => setTrialDays(event.target.value)} inputMode="numeric" className="nums" />
            )}
          </Field>
          <Field label="Politica la depășire" hint="Ce se întâmplă când se atinge limita.">
            {(props) => (
              <Select
                {...props}
                value={overageMode}
                onChange={(event) => setOverageMode(event.target.value as 'block' | 'soft' | 'notify')}
              >
                <option value="block">Blochează acțiunea</option>
                <option value="soft">Permite cu avertisment</option>
                <option value="notify">Doar notifică echipa</option>
              </Select>
            )}
          </Field>
          <div className="flex flex-col justify-center gap-3 pt-6">
            <Switch checked={isPublic} onChange={setIsPublic} label="Vizibil public" />
            <Switch checked={isActive} onChange={setIsActive} label="Plan activ" />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-body-s font-medium text-ink-950">Funcționalități incluse</span>
          <div className="flex flex-wrap gap-2">
            {features.map((feature) => {
              const active = selectedFeatures.includes(feature.code);
              return (
                <button
                  key={feature.code}
                  type="button"
                  onClick={() =>
                    setSelectedFeatures((current) =>
                      active ? current.filter((code) => code !== feature.code) : [...current, feature.code],
                    )
                  }
                  className={cx(
                    'flex items-center gap-2 rounded-full border px-3 py-1.5 text-caption transition-ui',
                    active ? 'border-brand-600 bg-brand-100 text-brand-700' : 'border-ink-200 text-ink-600 hover:border-ink-400',
                  )}
                >
                  {active ? <Icon name="check" size={13} /> : null}
                  {feature.name}
                  <span className="font-mono text-[10px] text-ink-400">{feature.code}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-body-s font-medium text-ink-950">Limite</span>
          <div className="grid gap-3 sm:grid-cols-3">
            {Object.keys(limits).map((key) => (
              <Field key={key} label={LIMIT_HINTS[key] ?? key} hint="-1 = nelimitat">
                {(props) => (
                  <Input
                    {...props}
                    value={limits[key] ?? ''}
                    onChange={(event) => setLimits((current) => ({ ...current, [key]: event.target.value }))}
                    inputMode="numeric"
                    className="nums"
                  />
                )}
              </Field>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ──────────────────────────── feature flags ──────────────────────────── */

export function AdminFlags() {
  const toast = useToast();
  const flags = useResource<FeatureFlagRow[]>(() => api.get<FeatureFlagRow[]>('/admin/feature-flags'));
  const plans = useResource<AdminPlan[]>(() => api.get<AdminPlan[]>('/admin/plans'));
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const planCodes = (plans.data ?? []).map((plan) => plan.code);

  async function patch(flag: FeatureFlagRow, body: Record<string, unknown>, successMessage: string) {
    setPending((current) => ({ ...current, [flag.code]: true }));
    try {
      await api.patch(`/admin/feature-flags/${flag.code}`, body);
      toast.success(successMessage);
      await flags.reload();
    } catch (error) {
      toast.error('Flag-ul nu a putut fi actualizat', errorMessage(error));
    } finally {
      setPending((current) => ({ ...current, [flag.code]: false }));
    }
  }

  const grouped = useMemo(() => {
    const map = new Map<string, FeatureFlagRow[]>();
    for (const flag of flags.data ?? []) {
      const list = map.get(flag.module) ?? [];
      list.push(flag);
      map.set(flag.module, list);
    }
    return [...map.entries()];
  }, [flags.data]);

  return (
    <div className="page-enter">
      <AdminHeader
        title="Feature flags"
        subtitle="Modulele se activează pe planuri sau pe conturi, fără o nouă versiune de cod. Cascada: locație → restaurant → cont → plan → global."
      />

      {flags.error ? (
        <InlineAlert tone="danger" title="Flag-urile nu au putut fi încărcate">
          {flags.error}
        </InlineAlert>
      ) : null}

      {flags.loading ? (
        <AdminCard>
          <AdminTableSkeleton rows={6} />
        </AdminCard>
      ) : (
        <div className="flex flex-col gap-4">
          {grouped.map(([module, moduleFlags]) => (
            <AdminCard key={module}>
              <h2 className="mb-4 font-mono text-caption uppercase tracking-[0.14em] text-ink-400">{module}</h2>
              <div className="flex flex-col gap-4">
                {moduleFlags.map((flag) => (
                  <div key={flag.id} className="flex flex-col gap-3 border-b border-white/5 pb-4 last:border-0 last:pb-0">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="flex items-center gap-2 text-body-s font-medium text-white">
                          {flag.name}
                          <span className="font-mono text-caption text-ink-400">{flag.code}</span>
                          {flag.overrides > 0 ? <Badge tone="warning">{flag.overrides} exceptări</Badge> : null}
                        </span>
                        <span className="text-caption text-ink-400">{flag.description}</span>
                      </div>
                      <div className="flex items-center gap-5">
                        <Switch
                          checked={flag.default_enabled}
                          onChange={(checked) =>
                            void patch(flag, { defaultEnabled: checked }, `Flag „${flag.code}” ${checked ? 'activat' : 'dezactivat'} implicit`)
                          }
                          label="Implicit"
                        />
                        <Switch
                          checked={flag.is_active}
                          onChange={(checked) => void patch(flag, { isActive: checked }, `Flag „${flag.code}” ${checked ? 'activ' : 'inactiv'}`)}
                          label="Activ"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-caption text-ink-400">Disponibil pe planurile:</span>
                      {planCodes.map((code) => {
                        const active = flag.plan_availability.includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            disabled={Boolean(pending[flag.code])}
                            onClick={() => {
                              const next = active
                                ? flag.plan_availability.filter((item) => item !== code)
                                : [...flag.plan_availability, code];
                              void patch(flag, { planAvailability: next }, `Planuri actualizate pentru „${flag.code}”`);
                            }}
                            className={cx(
                              'rounded-full border px-2.5 py-1 font-mono text-caption transition-ui disabled:opacity-50',
                              active ? 'border-brand-500 bg-brand-600/20 text-white' : 'border-white/15 text-ink-400 hover:border-white/30',
                            )}
                          >
                            {code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── audit ─────────────────────────────── */

interface AuditEntry {
  id: number;
  created_at: string;
  action: string;
  entity_type: string | null;
  entity_label: string | null;
  severity: 'info' | 'notice' | 'warning' | 'critical';
  actor_ip: string | null;
  actor_role: string | null;
  actor_email: string | null;
  account_name: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
}

const SEVERITY_TONE: Record<AuditEntry['severity'], 'neutral' | 'brand' | 'warning' | 'danger'> = {
  info: 'neutral',
  notice: 'brand',
  warning: 'warning',
  critical: 'danger',
};

export function AdminAudit() {
  const [severity, setSeverity] = useState('');
  const [action, setAction] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const query = new URLSearchParams({ limit: '100' });
  if (severity) query.set('severity', severity);
  if (action.trim()) query.set('action', action.trim());

  const logs = useResource<AuditEntry[]>(() => api.get<AuditEntry[]>(`/admin/audit-logs?${query.toString()}`), [severity, action]);

  return (
    <div className="page-enter">
      <AdminHeader
        title="Jurnal de audit"
        subtitle="Cine a făcut ce, când și de pe ce adresă. Intrările sunt append-only: nu pot fi modificate sau șterse."
        actions={
          <div className="flex flex-wrap gap-2">
            <Select
              value={severity}
              onChange={(event) => setSeverity(event.target.value)}
              className="h-10 w-40 border-white/15 bg-white/5 text-white"
            >
              <option value="">Toate nivelurile</option>
              <option value="info">info</option>
              <option value="notice">notice</option>
              <option value="warning">warning</option>
              <option value="critical">critical</option>
            </Select>
            <Input
              value={action}
              onChange={(event) => setAction(event.target.value)}
              placeholder="ex: auth.login"
              className="h-10 w-48 border-white/15 bg-white/5 font-mono text-white placeholder:text-ink-400"
            />
          </div>
        }
      />

      <AdminCard>
        {logs.loading ? (
          <AdminTableSkeleton rows={8} />
        ) : logs.error ? (
          <InlineAlert tone="danger" title="Jurnalul nu a putut fi încărcat">
            {logs.error}
          </InlineAlert>
        ) : !logs.data?.length ? (
          <EmptyState
            title="Nicio intrare pentru filtrele curente"
            description="Jurnalul se populează pe măsură ce utilizatorii lucrează în platformă: autentificări, modificări de meniu, schimbări de plan."
          />
        ) : (
          <AdminTable headers={['Când', 'Acțiune', 'Cine', 'Cont', 'Nivel', '']}>
            {logs.data.map((entry) => (
              <AuditRow
                key={entry.id}
                entry={entry}
                expanded={expanded === entry.id}
                onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
              />
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}

function AuditRow({ entry, expanded, onToggle }: { entry: AuditEntry; expanded: boolean; onToggle: () => void }) {
  const hasDetails = Boolean(entry.before_data || entry.after_data);
  return (
    <>
      <AdminRow tone={entry.severity === 'critical' ? 'danger' : undefined}>
        <Td>
          <span className="flex flex-col">
            <span className="text-white">{formatDateTime(entry.created_at)}</span>
            <span className="text-caption text-ink-400">{relativeTime(entry.created_at)}</span>
          </span>
        </Td>
        <Td>
          <span className="flex flex-col">
            <span className="text-white">{AUDIT_LABELS[entry.action] ?? entry.action}</span>
            <span className="font-mono text-caption text-ink-400">
              {entry.action}
              {entry.entity_label ? ` · ${entry.entity_label}` : ''}
            </span>
          </span>
        </Td>
        <Td>
          <span className="flex flex-col">
            <span>{entry.actor_email ?? 'sistem'}</span>
            <span className="font-mono text-caption text-ink-400">
              {entry.actor_role ?? '—'} · {entry.actor_ip ?? 'fără IP'}
            </span>
          </span>
        </Td>
        <Td>{entry.account_name ?? 'platformă'}</Td>
        <Td>
          <Badge tone={SEVERITY_TONE[entry.severity]}>{entry.severity}</Badge>
        </Td>
        <Td className="text-right">
          {hasDetails ? (
            <button
              type="button"
              onClick={onToggle}
              className="flex items-center gap-1 text-caption text-ink-200 transition-ui hover:text-white"
            >
              {expanded ? 'Ascunde' : 'Detalii'}
              <Icon name="chevron-down" size={14} className={cx('transition-ui', expanded && 'rotate-180')} />
            </button>
          ) : (
            <span className="text-caption text-ink-400">—</span>
          )}
        </Td>
      </AdminRow>
      {expanded ? (
        <tr className="border-b border-white/5">
          <td colSpan={6} className="pb-4">
            <pre className="max-h-64 overflow-auto rounded-[var(--radius-md)] border border-white/10 bg-black/30 p-3 font-mono text-[12px] leading-relaxed text-ink-200">
              {JSON.stringify({ before: entry.before_data, after: entry.after_data }, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/* ─────────────────────────────── sistem ─────────────────────────────── */

interface SystemEvent {
  id: number;
  level: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  channel: string;
  message: string;
  account_id: string | null;
  context: Record<string, unknown> | null;
  occurred_at: string;
}

const LEVEL_TONE: Record<SystemEvent['level'], 'neutral' | 'brand' | 'warning' | 'danger'> = {
  debug: 'neutral',
  info: 'brand',
  warning: 'warning',
  error: 'danger',
  critical: 'danger',
};

export function AdminSystemEvents() {
  const [level, setLevel] = useState('');
  const events = useResource<SystemEvent[]>(
    () => api.get<SystemEvent[]>(`/admin/system-events?limit=100${level ? `&level=${level}` : ''}`),
    [level],
  );

  return (
    <div className="page-enter">
      <AdminHeader
        title="Evenimente de sistem"
        subtitle="Semnalări interne: erori 5xx, joburi eșuate, limite atinse. Nu conțin date personale ale clienților."
        actions={
          <Select value={level} onChange={(event) => setLevel(event.target.value)} className="h-10 w-44 border-white/15 bg-white/5 text-white">
            <option value="">Toate nivelurile</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="critical">critical</option>
          </Select>
        }
      />

      <AdminCard>
        {events.loading ? (
          <AdminTableSkeleton rows={5} />
        ) : events.error ? (
          <InlineAlert tone="danger" title="Evenimentele nu au putut fi încărcate">
            {events.error}
          </InlineAlert>
        ) : !events.data?.length ? (
          <EmptyState
            icon={<Icon name="shield" size={26} />}
            title="Niciun eveniment de sistem"
            description="Aici apar erorile 5xx și problemele interne. Absența lor înseamnă că platforma rulează normal."
          />
        ) : (
          <AdminTable headers={['Când', 'Nivel', 'Canal', 'Mesaj', 'Cont']}>
            {events.data.map((event) => (
              <AdminRow key={event.id} tone={event.level === 'critical' ? 'danger' : undefined}>
                <Td>{formatDateTime(event.occurred_at)}</Td>
                <Td>
                  <Badge tone={LEVEL_TONE[event.level]}>{event.level}</Badge>
                </Td>
                <Td>
                  <span className="font-mono text-caption text-ink-400">{event.channel}</span>
                </Td>
                <Td>{event.message}</Td>
                <Td>
                  <span className="font-mono text-caption text-ink-400">{event.account_id ?? 'platformă'}</span>
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}

export type { FeatureFlagRow, AdminPlan, AuditEntry, SystemEvent };
